const SETUP_INTENT_FORM_KEY_PREFIX = "account-nux:setup-intent-form-key";
const SETUP_INTENT_FORM_KEY_TTL_MS = 23 * 60 * 60 * 1000;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type StoredSetupIntentFormKey = {
  formKey: string;
  createdAt: number;
};

/**
 * Returns the stable browser key for one organization's payment form. The key
 * survives NUX navigation and reloads, but expires before Stripe may forget its
 * 24-hour idempotency record.
 *
 * @param organizationId - Organization that owns the payment session.
 * @param now - Current Unix time in milliseconds, injectable for expiry tests.
 */
export function getSetupIntentFormKey(
  organizationId: string,
  now = Date.now(),
): string {
  const storageKey = `${SETUP_INTENT_FORM_KEY_PREFIX}:${organizationId}`;
  const existingValue = sessionStorage.getItem(storageKey);
  const existingKey = parseStoredFormKey(existingValue);
  if (
    existingKey &&
    existingKey.createdAt <= now &&
    now - existingKey.createdAt < SETUP_INTENT_FORM_KEY_TTL_MS
  ) {
    return existingKey.formKey;
  }

  const formKey = crypto.randomUUID();
  sessionStorage.setItem(
    storageKey,
    JSON.stringify({
      formKey,
      createdAt: now,
    } satisfies StoredSetupIntentFormKey),
  );
  return formKey;
}

/** Clears a payment-related rejected session so its replacement gets a new key. */
export function resetSetupIntentFormKey(organizationId: string): void {
  sessionStorage.removeItem(
    `${SETUP_INTENT_FORM_KEY_PREFIX}:${organizationId}`,
  );
}

function parseStoredFormKey(
  value: string | null,
): StoredSetupIntentFormKey | null {
  if (!value) return null;

  try {
    const parsed: unknown = JSON.parse(value);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "formKey" in parsed &&
      typeof parsed.formKey === "string" &&
      UUID_PATTERN.test(parsed.formKey) &&
      "createdAt" in parsed &&
      typeof parsed.createdAt === "number" &&
      Number.isSafeInteger(parsed.createdAt) &&
      parsed.createdAt >= 0
    ) {
      return { formKey: parsed.formKey, createdAt: parsed.createdAt };
    }
  } catch {
    // Invalid session state is replaced below rather than crossing the API boundary.
  }

  return null;
}
